import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { leiloeirosService } from '../../services/leiloeirosService';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/ui/Toast';
import NovoLeiloeiroModal from '../../components/modals/NovoLeiloeiroModal';
import EditarLeiloeiroModal from '../../components/modals/EditarLeiloeiroModal';

const LeiloeirosList = () => {
  const { organizadorAtivo } = useOrganizador();
  const { toasts, success, error, warning, hideToast } = useToast();
  const [leiloeiros, setLeiloeiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovaModal, setShowNovaModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [leiloeiroSelecionado, setLeiloeiroSelecionado] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);


  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarLeiloeiros();
    }
  }, [organizadorAtivo?.id]);

  const carregarLeiloeiros = async () => {
    if (!organizadorAtivo?.id) return;
    
    try {
      setLoading(true);
      const response = await leiloeirosService.listar(organizadorAtivo.id);
      setLeiloeiros(response.data);
    } catch (error) {
      console.error('Erro ao carregar leiloeiros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = (id, nome) => {
    setItemToDelete({ id, nome });
    setShowConfirmDelete(true);
  };

  const confirmarExclusao = async () => {
    if (!itemToDelete) return;
    
    try {
      await leiloeirosService.excluir(organizadorAtivo.id, itemToDelete.id);
      success('Leiloeiro excluído com sucesso!');
      await carregarLeiloeiros();
    } catch (err) {
      console.error('Erro ao excluir leiloeiro:', err);
      if (err.response?.status === 409) {
        error('Não é possível excluir este leiloeiro pois ele está sendo utilizado em leilões.');
      } else {
        error('Erro ao excluir leiloeiro');
      }
    }
    setItemToDelete(null);
    setShowConfirmDelete(false);
  };

  const handleEditar = (leiloeiro) => {
    setLeiloeiroSelecionado(leiloeiro);
    setShowEditarModal(true);
  };

  const formatarPercentual = (valor) => {
    return valor ? `${valor}%` : '-';
  };

  return (
    <>
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
      
      <div>
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Leiloeiros</h2>
        <button onClick={() => setShowNovaModal(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Novo </span>Leiloeiro
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Lista de Leiloeiros</h3>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : leiloeiros.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Nenhum leiloeiro encontrado</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                {leiloeiros.map((leiloeiro) => (
                  <div key={leiloeiro.id} className="p-4 bg-white">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {leiloeiro.is_padrao && <span className="text-yellow-400 text-sm">⭐</span>}
                          <span className="font-medium text-gray-900 text-sm truncate">{leiloeiro.nome}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          {leiloeiro.cpf && <span>{leiloeiro.cpf}</span>}
                          {leiloeiro.telefone && <span>{leiloeiro.telefone}</span>}
                          <span>V:{formatarPercentual(leiloeiro.perc_venda)} · D:{formatarPercentual(leiloeiro.perc_defesa)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${leiloeiro.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {leiloeiro.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                        <button onClick={() => handleEditar(leiloeiro)} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleExcluir(leiloeiro.id, leiloeiro.nome)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPF</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comissão</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {leiloeiros.map((leiloeiro) => (
                      <tr key={leiloeiro.id} className="hover:bg-gray-50">
                        <td className="px-2 py-4 whitespace-nowrap text-center">
                          {leiloeiro.is_padrao && <span title="Leiloeiro padrão" className="text-yellow-400 text-base">⭐</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{leiloeiro.nome}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{leiloeiro.cpf}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{leiloeiro.telefone}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">V:{formatarPercentual(leiloeiro.perc_venda)} D:{formatarPercentual(leiloeiro.perc_defesa)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${leiloeiro.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {leiloeiro.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button onClick={() => handleEditar(leiloeiro)} className="text-yellow-600 hover:text-yellow-900"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleExcluir(leiloeiro.id, leiloeiro.nome)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <NovoLeiloeiroModal
        isOpen={showNovaModal}
        onClose={() => setShowNovaModal(false)}
        onSave={async (data) => {
          await leiloeirosService.criar(organizadorAtivo.id, data);
          setShowNovaModal(false);
          await carregarLeiloeiros();
        }}
      />

      <EditarLeiloeiroModal
        isOpen={showEditarModal}
        onClose={() => setShowEditarModal(false)}
        leiloeiro={leiloeiroSelecionado}
        onSave={async (data) => {
          await leiloeirosService.atualizar(organizadorAtivo.id, leiloeiroSelecionado.id, data);
          setShowEditarModal(false);
          await carregarLeiloeiros();
        }}
      />

      {showConfirmDelete && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Excluir Leiloeiro
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o leiloeiro "{itemToDelete.nome}"? Esta ação não pode ser desfeita.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDelete(false);
                  setItemToDelete(null);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusao}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default LeiloeirosList;